import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useData } from '@/hooks/useData';
import { apiFetch } from '@/api';
import { useAuth } from '@/context/useAuth';
import { useNotification } from '@/context/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { hasPermission } from '@/utils/permissions';
// @ts-ignore
import { StandardPageLayout, LoadingSpinner, ConfirmModal } from '../../components/Common.jsx';
import {
    LayoutGrid, List, PlusCircle, Search, Filter, MoreHorizontal,
    FileJson, Download, Trash2, Edit, Copy, ExternalLink,
    Upload, Globe, Eye, Clock, Folder, History, GitBranch, Archive, RotateCcw, UploadCloud, FileText, ArrowRight
} from 'lucide-react';

import { useTimezoneFormatter } from '@/hooks/useTimezoneFormatter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PublishAppModal from '@/components/flows/PublishAppModal';
import { DashboardVersionHistoryPanel } from '@/components/DashboardStudio/DashboardVersionHistoryPanel';


interface DashboardsLandingPageProps {
    onCreateNew?: () => void;
    onEdit?: (id: string) => void;
    onView?: (id: string, dash: any) => void;
}

export default DashboardsLandingPage;

// ─────────────────────────────────────────
// Filter Dropdown — icon + checkbox popup
// ─────────────────────────────────────────
function FilterDropdown({ value, onChange }: { value: string; onChange: (v: string) => void }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const options = [
        { label: 'All Status', val: 'all', color: 'text-gray-600 dark:text-slate-300' },
        { label: 'Draft', val: 'draft', color: 'text-amber-600 dark:text-amber-400' },
        { label: 'Published', val: 'published', color: 'text-green-600 dark:text-green-400' },
        { label: 'Archived', val: 'archived', color: 'text-gray-400 dark:text-slate-500' },
    ];

    const activeLabel = options.find(o => o.val === value)?.label || 'All Status';
    const hasFilter = value !== 'all';

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen(!open)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-all shadow-sm ${
                    hasFilter
                        ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                        : 'bg-white border-gray-200 text-gray-600 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400'
                } hover:shadow-md`}
            >
                <Filter className="w-3.5 h-3.5" />
                <span>{activeLabel}</span>
                {hasFilter && (
                    <span className="ml-1 flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-[9px] font-bold">1</span>
                )}
            </button>
            {open && (
                <div className="absolute left-0 top-full mt-1.5 z-50 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-gray-100 dark:border-slate-800 py-2 min-w-[160px]">
                    <p className="px-3 py-1 text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">FILTER BY STATUS</p>
                    {options.map(opt => (
                        <button
                            key={opt.val}
                            onClick={() => { onChange(opt.val); setOpen(false); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                        >
                            <span className={`w-4 h-4 rounded flex items-center justify-center border-2 transition-colors ${
                                value === opt.val
                                    ? 'bg-blue-600 border-blue-600'
                                    : 'border-gray-300 dark:border-slate-600'
                            }`}>
                                {value === opt.val && (
                                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10">
                                        <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </span>
                            <span className={opt.color + ' font-medium'}>{opt.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
function DashboardsLandingPage({
    onCreateNew,
    onEdit,
    onView
}: DashboardsLandingPageProps) {
    const { token, user } = useAuth();
    const { formatDate: formatTzDate } = useTimezoneFormatter();
    const { addNotification } = useNotification();

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        addNotification("Copied to clipboard", "success");
    };


    // Permissions
    const isAdmin = ['r1', 'r2', 'r7'].includes(user?.roleId);

    // UI State
    const [viewMode, _setViewMode] = useState<'grid' | 'list'>(() => {
        return (localStorage.getItem('dashboards-view-mode') as 'grid' | 'list') || 'grid';
    });
    const setViewMode = (mode: 'grid' | 'list') => {
        localStorage.setItem('dashboards-view-mode', mode);
        _setViewMode(mode);
    };
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const showArchived = statusFilter === 'archived';
    const { data: dashboards, isLoading, refresh } = useData(showArchived ? '/dashboards?include_archived=true' : '/dashboards');
    const navigate = useNavigate();
    const [publishModal, setPublishModal] = useState<any>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<any>(null);
    const [versionHistoryDash, setVersionHistoryDash] = useState<any>(null);
    const importInputRef = useRef<HTMLInputElement>(null);
    const [importPayload, setImportPayload] = useState<any>(null);
    const [importTargetDataModelId, setImportTargetDataModelId] = useState<string>('none');
    const { data: dataModels } = useData('/datamodels');

    // Description editor modal
    const [descModal, setDescModal] = useState<{ id: string; name: string } | null>(null);
    const [editingDesc, setEditingDesc] = useState('');
    const [savingDesc, setSavingDesc] = useState(false);
    // Version history panel state (opened directly from landing page, no edit navigation)
    const [versionHistoryDashId, setVersionHistoryDashId] = useState<string | null>(null);

    const saveDescription = async () => {
        if (!descModal) return;
        setSavingDesc(true);
        try {
            await apiFetch(`/dashboards/${descModal.id}`, token, { method: 'PATCH', body: JSON.stringify({ description: editingDesc.trim() }) });
            refresh();
            addNotification('Description saved', 'success');
            setDescModal(null);
        } catch {
            addNotification('Failed to save description', 'error');
        } finally {
            setSavingDesc(false);
        }
    };

    // Publish modal: version auto-suggestion
    const [nextVersionLabel, setNextVersionLabel] = useState<string>('v1');

    // Fetch live versions to compute the next label, then open the modal
    const openPublishModal = async (dash: any) => {
        let label = 'v1';
        try {
            const versions = await apiFetch(`/dashboards/${dash.id}/versions`, token);
            const maxVer = Array.isArray(versions) && versions.length > 0
                ? Math.max(...versions.map((v: any) => v.version_number || 0))
                : 0;
            label = `v${maxVer + 1}`;
        } catch { /* fall through to v1 */ }
        setNextVersionLabel(label);
        setPublishModal(dash);
    };

    // Refresh landing page data when any dashboard is published from the editor
    useEffect(() => {
        const handler = () => refresh();
        window.addEventListener('dash:published', handler);
        return () => window.removeEventListener('dash:published', handler);
    }, [refresh]);

    // Filter dashboards by search and status
    const filteredDashboards = dashboards?.filter((d: any) => {
        const matchesSearch = d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            d.description?.toLowerCase().includes(searchTerm.toLowerCase());
        let matchesStatus = true;
        if (statusFilter === 'published') matchesStatus = d.status === 'published';
        else if (statusFilter === 'draft') matchesStatus = d.status !== 'published' && d.status !== 'archived';
        else if (statusFilter === 'archived') matchesStatus = d.status === 'archived';
        // 'all' → matchesStatus stays true (show everything returned by the API)
        return matchesSearch && matchesStatus;
    }) || [];

    // === GROUPING LOGIC ===
    const groupedDashboards = useMemo(() => {
        const groups: Record<string, any[]> = {};
        const ungrouped: any[] = [];

        filteredDashboards.forEach((d: any) => {
            if (d.folder_name) {
                if (!groups[d.folder_name]) groups[d.folder_name] = [];
                groups[d.folder_name].push(d);
            } else {
                ungrouped.push(d);
            }
        });

        // Sort folders alphabetically
        const sortedGroups = Object.keys(groups).sort().reduce((acc: any, key) => {
            acc[key] = groups[key];
            return acc;
        }, {});

        return { groups: sortedGroups, ungrouped };
    }, [filteredDashboards]);

    // === HANDLERS ===
    const handleCreateNew = () => {
        if (onCreateNew) onCreateNew();
        else navigate('/dashboards/create');
    };

    const handleEdit = (dashboard: any) => {
        if (onEdit) onEdit(dashboard.id);
        else navigate(`/dashboards/${dashboard.id}/edit`);
    };

    // Open the version history panel directly on the landing page — no edit navigation.
    const handleVersionHistory = (dashboard: any) => {
        setVersionHistoryDashId(dashboard.id);
    };

    const handleView = (dashboard: any) => {
        if (onView) onView(dashboard.id, dashboard);
        else navigate(`/dashboards/${dashboard.id}/view`);
    };

    const handleClone = async (dash: any) => {
        try {
            const { id, ...rest } = dash;
            const cloned = {
                ...rest,
                name: `${dash.name} (Copy)`,
                status: 'draft'
            };
            await apiFetch('/dashboards', token, { method: 'POST', body: JSON.stringify(cloned) });
            refresh();
            addNotification("Dashboard cloned", "success");
        } catch (e: any) {
            console.error('Clone failed:', e);
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        try {
            await apiFetch(`/dashboards/${deleteConfirm.id}`, token, { method: 'DELETE' });
            addNotification("Dashboard deleted", "success");
            refresh();
        } catch (e: any) {
            console.error('Delete failed:', e);
            addNotification(e.message || "Delete failed", "error");
        } finally {
            setDeleteConfirm(null);
        }
    };

    const handleArchive = async (dash: any) => {
        try {
            await apiFetch(`/dashboards/${dash.id}/archive`, token, { method: 'POST' });
            addNotification(`"${dash.name}" archived`, 'success');
            refresh();
        } catch (e: any) {
            addNotification(e.message || 'Archive failed', 'error');
        }
    };

    const handleRestore = async (dash: any) => {
        try {
            await apiFetch(`/dashboards/${dash.id}/restore`, token, { method: 'POST' });
            addNotification(`"${dash.name}" restored`, 'success');
            refresh();
        } catch (e: any) {
            addNotification(e.message || 'Restore failed', 'error');
        }
    };

    const handleExport = async (dash: any) => {
        try {
            const res = await apiFetch(`/dashboards/${dash.id}/export`, token, { method: 'POST' });
            const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(res, null, 2));
            const downloadAnchor = document.createElement('a');
            downloadAnchor.setAttribute("href", dataStr);
            downloadAnchor.setAttribute("download", `${dash.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_export.json`);
            document.body.appendChild(downloadAnchor);
            downloadAnchor.click();
            downloadAnchor.remove();
        } catch (e: any) {
            console.error('Export failed:', e);
        }
    };

    const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (ev) => {
            try {
                const json = JSON.parse(ev.target?.result as string);
                setImportPayload(json);
                setImportTargetDataModelId('none');
            } catch (err: any) {
                console.error('Import parse failed:', err);
                addNotification("Import payload parse failed", "error");
            }
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    const confirmImport = async () => {
        if (!importPayload) return;
        
        try {
            const payload = JSON.parse(JSON.stringify(importPayload));

            if (importTargetDataModelId && importTargetDataModelId !== 'none') {
                const replaceDataModelIds = (obj: any, targetId: string) => {
                    if (Array.isArray(obj)) {
                        obj.forEach(item => replaceDataModelIds(item, targetId));
                    } else if (obj !== null && typeof obj === 'object') {
                        if ('dataModelId' in obj) obj.dataModelId = targetId;
                        if ('dataset_id' in obj) obj.dataset_id = targetId;
                        for (const key of Object.keys(obj)) {
                            replaceDataModelIds(obj[key], targetId);
                        }
                    }
                };
                replaceDataModelIds(payload, importTargetDataModelId);
                
                // Strip out the payload dependencies so the backend doesn't recreate the source Data Model
                if (payload.dependencies && Array.isArray(payload.dependencies)) {
                    payload.dependencies = payload.dependencies.filter((dep: any) => dep.type !== 'datamodel');
                }
            }

            const requestBody = {
                payload: payload,
                config: { connection_mappings: {} }
            };

            await apiFetch('/dashboards/import-json', token, { method: 'POST', body: JSON.stringify(requestBody) });
            refresh();
            addNotification("Dashboard imported", "success");
            setImportPayload(null);
            setImportTargetDataModelId('none');
        } catch (err: any) {
            console.error('Import failed:', err);
            addNotification("Import failed", "error");
        }
    };

    const handleUnpublish = async (dash: any) => {
        try {
            await apiFetch(`/dashboards/${dash.id}/unpublish`, token, { method: 'POST' });
            addNotification(`"${dash.name}" unpublished — back to Draft`, 'success');
            refresh();
        } catch (e: any) {
            addNotification(e.message || 'Unpublish failed', 'error');
        }
    };

    const handlePublish = async (dash: any) => {
        const newStatus = dash.status === 'published' ? 'draft' : 'published';
        try {
            await apiFetch(`/dashboards/${dash.id}`, token, {
                method: 'PUT',
                body: JSON.stringify({ status: newStatus })
            });
            refresh();
            addNotification(`Dashboard ${newStatus === 'published' ? 'published' : 'unpublished'}`, "success");
        } catch (e: any) {
            console.error('Publish failed:', e);
        }
    };

    const handleConfirmPublish = async (
        name: string,
        description: string,
        contentTypeId: number,
        sharedWith: string[],
        folderName: string,
        status: string,
        _overwrite: boolean,
        _linkedDashId: string,
        versionLabel: string
    ) => {
        if (!publishModal) return;

        try {
            await apiFetch(`/dashboards/${publishModal.id}/publish`, token, {
                method: 'POST',
                body: JSON.stringify({
                    type: 'dashboard',
                    name,
                    description,
                    contentTypeId,
                    sharedWith,
                    folderName,
                    status: status || 'Published',
                    versionLabel: versionLabel || undefined,
                })
            });
            setPublishModal(null);
            refresh();
            addNotification("Dashboard published successfully", "success");
        } catch (e: any) {
            console.error("Publish failed", e);
            addNotification(e.message || "Publish failed", "error");
        }
    };


    // Format date
    const formatDate = (dateStr: string) => {
        if (!dateStr) return '-';
        return formatTzDate(dateStr);
    };

    // Header Actions
    const headerActions = (
        <div className="flex items-center gap-2">
            <input
                type="file"
                ref={importInputRef}
                className="hidden"
                accept=".json"
                onChange={handleImport}
                style={{ display: 'none' }}
            />
            <Button onClick={handleCreateNew}>
                <PlusCircle className="mr-2 h-4 w-4" /> CREATE DASHBOARD
            </Button>
            {hasPermission(user, 'dashboards:import') && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => importInputRef.current?.click()}>
                            <Upload className="mr-2 h-4 w-4" /> Import JSON
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </div>
    );

    // Filter Controls
    const filterControls = (
        <div className="flex items-center gap-4">
            <div className="relative max-w-md group w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-[#002F5D] dark:text-slate-500 transition-colors" />
                <Input
                    type="text"
                    placeholder="Search dashboards..."
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
            <FilterDropdown value={statusFilter} onChange={setStatusFilter} />

            <div className="flex items-center bg-slate-100 dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-800 p-1 ml-auto">
                <Button variant="ghost" size="sm" onClick={() => setViewMode('grid')} className={`px-2 h-8 transition-all duration-200 ${viewMode === 'grid' ? 'bg-white text-blue-700 shadow-sm dark:bg-slate-800 dark:text-blue-400 font-semibold' : 'text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200'}`}>
                    <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setViewMode('list')} className={`px-2 h-8 transition-all duration-200 ${viewMode === 'list' ? 'bg-white text-blue-700 shadow-sm dark:bg-slate-800 dark:text-blue-400 font-semibold' : 'text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200'}`}>
                    <List className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );

    const renderCard = (dash: any) => {
        const isPublished = dash.status === 'published';
        const isArchived = dash.status === 'archived';
        const versionLabel = dash.version_number ? `v${dash.version_number}` : 'v1';
        return (
            <div
                key={dash.id}
                className={`group rounded-xl border shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 cursor-pointer relative overflow-hidden flex flex-col ${
                    isArchived
                        ? 'bg-gray-50 dark:bg-slate-950 border-gray-200 dark:border-slate-800 opacity-70 grayscale-[30%]'
                        : 'bg-white dark:bg-slate-900 border-gray-100 dark:border-slate-800'
                }`}
                onClick={() => handleView(dash)}
            >
                {/* Top accent bar – green on published (faint always, vivid on hover), blue on draft hover only */}
                <div className={`absolute top-0 left-0 w-full h-1 transition-all duration-300 ${
                    isPublished
                        ? 'bg-gradient-to-r from-green-400 to-emerald-400 opacity-20 group-hover:opacity-100'
                        : 'bg-gradient-to-r from-blue-600 to-cyan-400 opacity-0 group-hover:opacity-100'
                }`} />

                {/* Card top: just 3-dot menu */}
                <div className="flex items-center justify-end px-3 pt-2.5 pb-0">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300" onClick={(e) => e.stopPropagation()}>
                                <MoreHorizontal className="w-4 h-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(dash); }}>
                                    <Edit className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditingDesc(dash.description || ''); setDescModal({ id: dash.id, name: dash.name }); }}>
                                    <FileText className="w-4 h-4 mr-2 text-blue-400" /> Description
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleVersionHistory(dash); }}>
                                    <History className="w-4 h-4 mr-2" /> Version History
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleClone(dash); }}>
                                    <Copy className="w-4 h-4 mr-2" /> Clone
                                </DropdownMenuItem>
                                {hasPermission(user, 'dashboards:export') && (
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleExport(dash); }}>
                                        <Download className="w-4 h-4 mr-2" /> Export
                                    </DropdownMenuItem>
                                )}
                                {!isPublished && (
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openPublishModal(dash); }}>
                                        <Globe className="w-4 h-4 mr-2" /> Publish...
                                    </DropdownMenuItem>
                                )}
                                {isPublished && (
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleUnpublish(dash); }} className="text-amber-600 dark:text-amber-400">
                                        <UploadCloud className="w-4 h-4 mr-2" /> Unpublish
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={(e) => { 
                                    e.stopPropagation(); 
                                    navigator.clipboard.writeText(dash.id);
                                    addNotification("Dashboard ID copied to clipboard", "success");
                                }}>
                                    <Copy className="w-4 h-4 mr-2" /> Copy ID
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {dash.status === 'archived' ? (
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleRestore(dash); }} className="text-blue-600 dark:text-blue-400">
                                        <RotateCcw className="w-4 h-4 mr-2" /> Restore
                                    </DropdownMenuItem>
                                ) : (
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleArchive(dash); }} className="text-amber-600 dark:text-amber-400">
                                        <Archive className="w-4 h-4 mr-2" /> Archive
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setDeleteConfirm(dash); }} className="text-red-600">
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                </div>

                {/* Card body */}
                <div className="px-4 pt-3 pb-4 flex flex-col flex-grow">
                    {/* Icon + title + date */}
                    <div className="flex items-start gap-3 mb-3">
                        <div className={`p-2 rounded-lg shrink-0 transition-colors duration-200 ${
                            isPublished
                                ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 group-hover:bg-green-500 group-hover:text-white'
                                : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 group-hover:bg-[#002F5D] group-hover:text-white'
                        }`}>
                            <LayoutGrid className="h-5 w-5" />
                        </div>
                        <div className="overflow-hidden flex-1">
                            <h3 className={`font-bold text-gray-900 dark:text-slate-100 transition-colors truncate text-sm ${
                                isPublished ? 'group-hover:text-green-600 dark:group-hover:text-green-400' : 'group-hover:text-blue-600 dark:group-hover:text-blue-400'
                            }`} title={dash.name}>
                                {dash.name}
                            </h3>
                            <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                                <Clock className="w-3 h-3" />
                                {formatDate(dash.updated_at || dash.created_at)}
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <p className="text-xs text-gray-500 dark:text-slate-400 line-clamp-2 mb-3 min-h-[32px]">
                        {dash.description || 'No description provided'}
                    </p>

                    {/* Status + version row — mirrors Flow Studio */}
                    <div className="flex items-center gap-2 mt-auto">
                        {isPublished ? (
                            <Badge className="text-[11px] h-5 px-2 bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800 gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                                Published
                            </Badge>
                        ) : (
                            <Badge className="text-[11px] h-5 px-2 bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800 gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                                Draft
                            </Badge>
                        )}
                        <Badge variant="outline" className="text-[11px] h-5 px-2 font-mono gap-1 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400">
                            <GitBranch className="w-2.5 h-2.5" />
                            {versionLabel}
                        </Badge>
                    </div>

                    {/* Quick-publish for draft */}
                    {!isPublished && (
                        <button
                            className="mt-3 w-full text-xs border border-dashed border-blue-200 dark:border-blue-900 text-blue-500 dark:text-blue-400 rounded-lg py-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center justify-center gap-1"
                            onClick={(e) => { e.stopPropagation(); setPublishModal(dash); }}
                        >
                            <Globe className="w-3 h-3" /> Publish
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderListView = (items: any[]) => (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
            <Table>
                <TableHeader className="bg-gray-50/50 dark:bg-slate-900/50">
                    <TableRow className="border-b border-gray-200 dark:border-slate-800">
                        <TableHead>Dashboard Name</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Last Modified</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Version</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {items.map(dash => {
                        const isPublished = dash.status === 'published';
                        const versionLabel = dash.version_number ? `v${dash.version_number}` : 'v1';
                        return (
                            <TableRow key={dash.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-gray-100 dark:border-slate-800 cursor-pointer" onClick={() => handleView(dash)}>
                                <TableCell className="font-medium text-gray-900 dark:text-slate-100">
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center shrink-0">
                                            <LayoutGrid className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <div className="font-bold">{dash.name}</div>
                                            <div className="text-xs text-gray-400 font-mono hidden md:block" title="Copy ID" onClick={(e) => { e.stopPropagation(); copyToClipboard(dash.id); }}>{dash.id}</div>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell className="max-w-[300px] truncate text-gray-500">{dash.description || '-'}</TableCell>
                                <TableCell className="text-gray-500 whitespace-nowrap">{formatDate(dash.updated_at || dash.created_at)}</TableCell>
                                <TableCell>
                                    {isPublished ? (
                                        <Badge className="text-[11px] h-5 px-2 bg-green-100 text-green-700 border border-green-200 gap-1">
                                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" /> Published
                                        </Badge>
                                    ) : (
                                        <Badge className="text-[11px] h-5 px-2 bg-amber-50 text-amber-700 border border-amber-200 gap-1">
                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" /> Draft
                                        </Badge>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Badge variant="outline" className="text-[11px] h-5 px-2 font-mono gap-1 text-gray-500">
                                        <GitBranch className="w-2.5 h-2.5" />{versionLabel}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300" onClick={(e) => e.stopPropagation()}>
                                                <MoreHorizontal className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleView(dash); }}>
                                                <Eye className="w-4 h-4 mr-2" /> Open
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(dash); }}>
                                                <Edit className="w-4 h-4 mr-2" /> Edit
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleClone(dash); }}>
                                                <Copy className="w-4 h-4 mr-2" /> Clone
                                            </DropdownMenuItem>
                                            {hasPermission(user, 'dashboards:export') && (
                                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleExport(dash); }}>
                                                    <Download className="w-4 h-4 mr-2" /> Export
                                                </DropdownMenuItem>
                                            )}
                                            {!isPublished && (
                                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setPublishModal(dash); }}>
                                                    <Globe className="w-4 h-4 mr-2" /> Publish...
                                                </DropdownMenuItem>
                                            )}
                                            {isPublished && (
                                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleUnpublish(dash); }} className="text-amber-600 dark:text-amber-400">
                                                    <UploadCloud className="w-4 h-4 mr-2" /> Unpublish
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleVersionHistory(dash); }}>
                                                <History className="w-4 h-4 mr-2 text-blue-500" /> Version History
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setDeleteConfirm(dash); }} className="text-red-600">
                                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );


    return (
        <>
        <StandardPageLayout
            title="Dashboard Studio"
            description="Create and manage your custom analytics dashboards."
            headerActions={headerActions}
            filterControls={filterControls}
        >
            {isLoading ? <LoadingSpinner size={40} /> : (
                <div className="pb-10 min-h-[50vh] space-y-8">
                    {filteredDashboards.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="bg-slate-50 dark:bg-slate-900/50 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                                <LayoutGrid className="h-10 w-10 text-slate-300 dark:text-slate-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                                No dashboards found
                            </h3>
                            <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-sm mx-auto">
                                {searchTerm
                                    ? 'Try adjusting your search query'
                                    : 'Create a new dashboard to visualize your data.'}
                            </p>
                            <Button variant="ghost" onClick={handleCreateNew} className="gap-2">
                                Create Dashboard <ArrowRight className="h-4 w-4" />
                            </Button>
                        </div>
                    ) : (
                        <>
                            {/* Render Folders */}
                            {Object.entries(groupedDashboards.groups).map(([folder, items]) => {
                                const typedItems = items as any[];
                                return (
                                    <div key={folder} className="space-y-4">
                                        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 border-b border-gray-100 dark:border-slate-800 pb-2">
                                            <div className="bg-blue-50 dark:bg-blue-900/20 p-1 rounded text-blue-600 dark:text-blue-400">
                                                <Folder className="w-4 h-4" />
                                            </div>
                                            <h3 className="font-semibold text-sm uppercase tracking-wide">{folder}</h3>
                                            <span className="text-xs bg-gray-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">{typedItems.length}</span>
                                        </div>

                                        {viewMode === 'list' ? (
                                            renderListView(typedItems)
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                                {typedItems.map((dash: any) => renderCard(dash))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Render Ungrouped */}
                            {groupedDashboards.ungrouped.length > 0 && (
                                <div className="space-y-4">
                                    {Object.keys(groupedDashboards.groups).length > 0 && (
                                        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 border-b border-gray-100 dark:border-slate-800 pb-2">
                                            <h3 className="font-semibold text-sm uppercase tracking-wide">General</h3>
                                        </div>
                                    )}

                                    {viewMode === 'list' ? (
                                        renderListView(groupedDashboards.ungrouped)
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                            {groupedDashboards.ungrouped.map((dash: any) => renderCard(dash))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                onConfirm={handleDelete}
                title={`Delete ${deleteConfirm?.name}?`}
                zIndex={1300}
            >
                Are you sure you want to delete this dashboard?
            </ConfirmModal>

            <Dialog open={!!importPayload} onOpenChange={(isOpen) => { if (!isOpen) { setImportPayload(null); setImportTargetDataModelId('none'); } }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Import Dashboard Options</DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <p className="text-sm text-gray-500">
                            Configure the target Data Model for the dashboard you are importing to automatically update its widgets. Leave blank to keep existing settings.
                        </p>
                        <div className="space-y-2">
                            <Label>Target Data Model (Replaces dataset_id & dataModelId)</Label>
                            <Select value={importTargetDataModelId} onValueChange={setImportTargetDataModelId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select Target Data Model (Optional)" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px]">
                                    <SelectItem value="none">-- Keep Existing / Do Not Replace --</SelectItem>
                                    {dataModels?.map((dm: any) => (
                                        <SelectItem key={dm.id} value={dm.id}>{dm.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setImportPayload(null); setImportTargetDataModelId('none'); }}>
                            Cancel
                        </Button>
                        <Button onClick={confirmImport}>
                            Import
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <PublishAppModal
                open={!!publishModal}
                onOpenChange={(open) => !open && setPublishModal(null)}
                onPublish={handleConfirmPublish}
                defaultName={publishModal?.name || ''}
                defaultDescription={publishModal?.description || ''}
                initialContentTypeId={3}
                initialVersionLabel={nextVersionLabel}
            />

            {/* Description edit dialog */}
            <Dialog open={!!descModal} onOpenChange={(open) => !open && setDescModal(null)}>
                <DialogContent className="sm:max-w-[420px] bg-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-slate-400" />
                            Edit Description
                        </DialogTitle>
                        {descModal && <p className="text-xs text-gray-400 mt-0.5">{descModal.name}</p>}
                    </DialogHeader>
                    <div className="py-2">
                        <Label htmlFor="dash-desc-edit" className="mb-2 block">Description</Label>
                        <Textarea
                            id="dash-desc-edit"
                            value={editingDesc}
                            onChange={(e) => setEditingDesc(e.target.value)}
                            placeholder="Describe what this dashboard shows..."
                            className="h-28 resize-none"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDescModal(null)}>Cancel</Button>
                        <Button onClick={saveDescription} disabled={savingDesc} className="bg-blue-600 hover:bg-blue-700">
                            {savingDesc ? 'Saving…' : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            </StandardPageLayout>

            {/* Version History slide-over — opened directly from card/list menu, no edit navigation */}
            {versionHistoryDashId && (
                <DashboardVersionHistoryPanel
                    dashboardId={versionHistoryDashId}
                    isOpen={!!versionHistoryDashId}
                    onClose={() => setVersionHistoryDashId(null)}
                    // No onLoadSnapshot — read-only view on landing page
                />
            )}

        </>
    );
}
